import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { push } from 'connected-react-router';

import { BaseCrud, FormSection, FormSectionColumn } from 'react/containers/layout/Crud';
import MultipleField from 'react/components/SamlConfiguration/MultipleField';
import generalInformation from 'react/components/SamlConfiguration/GeneralInformation';
import fieldMapping from 'react/components/SamlConfiguration/FieldMapping';
import metadata from 'react/components/SamlConfiguration/Metadata';
import usersCreationAndUpdate from 'react/components/SamlConfiguration/UsersCreationAndUpdate';

import {
  TextField,
  CheckboxField
} from 'react/components/fields';

import { getResourceTo } from 'react/utils/utils';
import { actionCreators } from 'react/api/actions/samlConfiguration';
import * as selectors from 'react/api/selectors/samlConfiguration';

function SamlConfigurationCrud(props) {
  const dispatch = useDispatch();
  const {
    id,
    entity,
    fetchInitialFormValuesAction
  } = props;

  const isEdit = !(id === null || id.includes('SamlConfiguration'));

  useEffect(() => {
    if (isEdit) {
      const samlIdpURL = `/saml/idp/${id}`;
      fetchInitialFormValuesAction({ samlIdpURL, entity });
    }
  }, [isEdit]);

  const renderFieldsInGrid = () => (
    <FormSection title="SamlGrid fields" className="SamlGridFields">
      <FormSectionColumn>
        <MultipleField>
          <TextField
            description="idpName"
            label="samlConfiguration.idpName"
            name="idpName"
          />
        </MultipleField>
        <CheckboxField
          name="createUsers"
          label="samlConfiguration.createUsers"
        />
        <CheckboxField
          name="updateUsers"
          label="samlConfiguration.updateUsers"
        />
      </FormSectionColumn>
    </FormSection>
  );

  function Sidebar() {
    const sidebarTitle = getResourceTo('SamlConfiguration');
    const {
      page,
      firstStatusIsComplete,
      secondStatusIsComplete,
      thirdStatusIsComplete
    } = props;

    return (
      <div className="wizardSidebar">
        <h3 className="title">{sidebarTitle}</h3>
        <div
          className={`firstStep ${page === 0 ? 'actualSidebarSection' : 'sidebarSection'} ${firstStatusIsComplete ? 'valid' : ''}`}
          key="0"
        >
          <div className="leftBorder" />
          <div className="circle" />
          <span className="name">{getResourceTo('generalInformation.section.title')}</span>
        </div>
        <div
          className={`secondStep ${page === 1 ? 'actualSidebarSection' : 'sidebarSection'} ${secondStatusIsComplete ? 'valid' : ''}`}
          key="1"
        >
          <div className="leftBorder" />
          <div className="circle" />
          <span className="name">Metadata</span>
        </div>
        <div
          className={`thirdStep ${page === 2 ? 'actualSidebarSection' : 'sidebarSection'} ${thirdStatusIsComplete ? 'valid' : ''}`}
          key="2"
        >
          <div className="leftBorder" />
          <div className="circle" />
          <span className="name">Users creation and update</span>
        </div>
        <div
          className={`fourthStep ${page === 3 ? 'actualSidebarSection' : 'sidebarSection'} ${isEdit ? 'valid' : ''}`}
          key="3"
        >
          <div className="leftBorder" />
          <div className="circle" />
          <span className="name">Field mapping</span>
        </div>
      </div>
    );
  }

  const goToGrid = () => {
    dispatch(push(`/${entity}`));
  };

  const customRequest = (data) => {
    const { saveFormAction } = props;
    // eslint-disable-next-line no-new
    saveFormAction({ data, goToGrid });
  };

  const beforeSubmit = (values) => {
    const {
      idp: {
        idpName,
        idpThemeColor
      },
      idpCertificate,
      idpURL
    } = values;

    const { page } = props;

    const isMandatoryFieldsValid = idpName && idpThemeColor && idpURL && idpCertificate;

    if (isEdit && isMandatoryFieldsValid) {
      return values;
    }

    if (page === 1 && !idpCertificate) {
      return false;
    }

    if (!isEdit) {
      return values;
    }

    return false;
  };

  const sections = [
    generalInformation(props),
    metadata(props),
    usersCreationAndUpdate(props),
    fieldMapping(props)
  ];

  useEffect(() => {
    App.Objects.SamlConfiguration = new App.Routers.SamlConfiguration();
  }, []);

  return (
    <BaseCrud
      tabName="SamlConfiguration"
      entity="SamlConfiguration"
      updateMethod="PUT"
      customUrl="/saml/idp"
      isWizard
      sidebar={Sidebar}
      sections={sections}
      beforeSubmit={beforeSubmit}
      customRequest={customRequest}
      {...props}
    >
      {renderFieldsInGrid()}
    </BaseCrud>
  );
}

SamlConfigurationCrud.propTypes = {
  id: PropTypes.string,
  entity: PropTypes.string.isRequired,
  page: PropTypes.number.isRequired,
  firstStatusIsComplete: PropTypes.bool.isRequired,
  secondStatusIsComplete: PropTypes.bool.isRequired,
  thirdStatusIsComplete: PropTypes.bool.isRequired,
  saveFormAction: PropTypes.func.isRequired,
  fetchInitialFormValuesAction: PropTypes.func.isRequired
};

SamlConfigurationCrud.defaultProps = {
  id: null
};

const selector = formValueSelector('SamlConfiguration');

const mapDispatchToProps = {
  openOrganizationFieldsModalAction: actionCreators.openOrganizationFieldsModal,
  toggleOrganizationFieldsModalAction: actionCreators.toggleOrganizationFieldsModal,
  openLocaleKeyFieldsModalAction: actionCreators.openLocaleKeyFieldsModal,
  toggleLocaleKeyFieldsModalAction: actionCreators.toggleLocaleKeyFieldsModal,
  openGroupsFieldsModalAction: actionCreators.openGroupsFieldsModal,
  toggleGroupsFieldsModalAction: actionCreators.toggleGroupsFieldsModal,
  openUserTagFieldsModalAction: actionCreators.openUserTagFieldsModal,
  toggleUserTagFieldsModalAction: actionCreators.toggleUserTagFieldsModal,
  initializeIdpUrlValidationAction: actionCreators.initializeIdpUrlValidation,
  stopIdpUrlValidationAction: actionCreators.stopIdpUrlValidation,
  confirmIdpUrlValidationAction: actionCreators.confirmIdpUrlValidation,
  saveFormAction: actionCreators.saveForm,
  fetchInitialFormValuesAction: actionCreators.fetchInitialFormValues
};

export default connect(
  (state) => ({
    firstStatusIsComplete: selectors.isFirstSectionStatusComplete(state),
    secondStatusIsComplete: selectors.isSecondSectionStatusComplete(state),
    thirdStatusIsComplete: selectors.isThirdSectionStatusComplete(state),
    themeColor: selector(state, 'idp.idpThemeColor'),
    idpName: selector(state, 'idp.idpName'),
    idpURL: selector(state, 'idpURL'),
    idpCertificate: selector(state, 'idpCertificate'),
    createUsers: selector(state, 'idp.createUsers'),
    updateUsers: selector(state, 'idp.updateUsers'),
    userOrganizationDefaultValue: selector(
      state,
      'idp.fieldMappings.userOrganizationKey.defaultValue'
    ),
    organizationModalIsOpen: state.samlConfiguration.getIn(['modals', 'organization', 'modalIsOpen'], false),
    localeKeyModalIsOpen: state.samlConfiguration.getIn(['modals', 'localeKey', 'modalIsOpen'], false),
    groupsModalIsOpen: state.samlConfiguration.getIn(['modals', 'groups', 'modalIsOpen'], false),
    userTagModalIsOpen: state.samlConfiguration.getIn(['modals', 'userTag', 'modalIsOpen'], false),
    isValidating: state.samlConfiguration.getIn(['metadata', 'isLoading'], false),
    isValid: state.samlConfiguration.getIn(['metadata', 'isValid'], false),
    isInvalid: state.samlConfiguration.getIn(['metadata', 'isInvalid'], false),
    idpValidationError: state.samlConfiguration.getIn(['metadata', 'error'], ''),
    page: state.wizard.getIn(['SamlConfiguration', 'page'], 0)
  }
  ),
  mapDispatchToProps
)(SamlConfigurationCrud);
